import { Reducer } from "redux";
import { IService, emptyService } from "../../models/iservice"
import { IActionSigned, IServiceQuestion } from "../../models/iserviceQuestion";
import { IGroup, IQuestionTreeMap } from "./createService";
import * as actionTypes from '../actions/actionTypes';
import { updateObject } from "../../utils/updateObject";
import { createQuestionTreeForEditService } from "../../utils/createQuestionTree";
import { IAction as IActionModel} from "../../models/iaction";
import { IFile } from "../../UI/FileList";
import { IOldAction } from "../../components/ServiceOldActionsPanel/ServiceOldActionsPanel";
import moment, { DurationInputArg1, DurationInputArg2 } from "moment";

export interface IAction<T> {
  type: string;
  payload: T;
}

export interface IEditServiceState {
  serviceLoading: boolean,
  selectedServiceId: string,
  selectedService: IService,
  questions: IServiceQuestion[],
  questionsTree: IGroup<IServiceQuestion>[],
  questionsTreeMap: IQuestionTreeMap,
  questionsLoading: boolean,
  checklistScore: string,
  numberOfQuestionsThatShouldHaveAction: number,
  files: IFile[],
  date: Date,
  expirationDate: Date | null,
  signature: {
    document: string,
    name: string,
    url: string,
    source: string,
  },
  description: string,
  postServiceLoading: boolean,
  oldActions: IOldAction[],
  reportLoading: boolean,
}

const initialState: IEditServiceState = {
  serviceLoading: false,
  selectedServiceId: '',
  selectedService: emptyService,
  questions: [],
  questionsTree: [],
  questionsTreeMap: {},
  questionsLoading: false,
  checklistScore: '',
  numberOfQuestionsThatShouldHaveAction: 0,
  files: [],
  date: new Date(),
  expirationDate: null,
  signature: {
    document: '',
    name: '',
    url: '',
    source: '',
  },
  description: '',
  postServiceLoading: false,
  oldActions: [],
  reportLoading: false,
}

const selectService = (state: IEditServiceState, action: IAction<string>) => {
  return updateObject(state, { selectedServiceId: action.payload });
}

const fetchServiceByIdStart = (state: IEditServiceState): IEditServiceState => {
  return updateObject(state, { serviceLoading: true });
}

const fetchServiceByIdSuccess = (state: IEditServiceState, action: IAction<IService>): IEditServiceState => {
  const questions = action.payload.questions;
  
  let questionsTree: IGroup<IServiceQuestion>[] = [];
  let questionsTreeMap: IQuestionTreeMap = {};
  if (questions) {
    questionsTree = createQuestionTreeForEditService(questions);
    questionsTree.forEach((group, groupIndex) => {
      group.questionArray.forEach((question, questionIndex) => {
        questionsTreeMap[question._id] = [groupIndex, questionIndex];
      })
    });
  }

  let checklistScore: string = "0.00%"
  if (action.payload.checklistScoreFloat) checklistScore = `${action.payload.checklistScoreFloat.toFixed(2)}%`;

  return updateObject(state, {
    serviceLoading: false,
    selectedService: action.payload,
    questions,
    questionsTree,
    questionsTreeMap,
    checklistScore: checklistScore,
    description: action.payload.description,
    numberOfQuestionsThatShouldHaveAction: 0,
    date: new Date(action.payload.date),
    expirationDate: new Date(action.payload.expirationDate),
    files: action.payload.files,
    signature: {
      name: action.payload.signature?.name || '',
      document: action.payload.signature?.document || '',
      url: action.payload.signature?.file?.url || '',
      source: action.payload.signature?.file?.source || '',
    },
  });
}

const fetchServiceByIdFail = (state: IEditServiceState): IEditServiceState => {
  return updateObject(state, { serviceLoading: false });
}

const changeServiceDate = (state: IEditServiceState, action: IAction<Date>) => {
  const { amount, type, onDemand } = state.selectedService.equipmentService.frequency;
  const date = moment(action.payload)
    .set({'hour': 12, 'minute': 0, 'second': 0})
    .toDate();
  let expirationDate: Date |  null = null;
  if (!onDemand) {
    expirationDate = moment(action.payload)
      .add(amount as DurationInputArg1, type as DurationInputArg2)
      .set({'hour': 12, 'minute': 0, 'second': 0})
      .toDate();
  
    if (state.selectedService.equipmentService.expirationOnLastDayOfMonth) {
      expirationDate = moment(expirationDate)
      .endOf('month')
      .set({'hour': 12, 'minute': 0, 'second': 0})
      .toDate();
    } 
  }
  return updateObject(state, { date, expirationDate });
}

const changeServiceDescription = (state: IEditServiceState, action: IAction<string>) => {
  return updateObject(state, { description: action.payload });
}

const changeQuestionAnswer = (state: IEditServiceState, action: IAction<{ questionId: string, answer: string }>) => {
  const [groupIndex, questionIndex] = state.questionsTreeMap[action.payload.questionId];
  const questionsTree = [...state.questionsTree];
  questionsTree[groupIndex].questionArray[questionIndex].answer = action.payload.answer;

  let checklistScore: number = 0;
  let checklistTotalScore: number = 0;

  let answeredQuestions: number = 0;
  let numberOfQuestionsThatShouldHaveAction: number = 0;
  
  questionsTree.forEach((group) => {
    group.score = 0;
    group.totalScore = 0;
    group.questionArray.forEach((question) => {
      if (question.answer === 'NO') {
        group.totalScore += question.weight;
        if (state.selectedService.equipmentService.mandatoryActionsForNotComplyQuestions && question.oldActions?.length === 0 && question.actions.length === 0) {
          numberOfQuestionsThatShouldHaveAction += 1;
        }
      }
      else if (question.answer === 'YES') {
        group.score += question.weight;
        group.totalScore += question.weight;
        
      } 
      if (question.answer !== "") {
        answeredQuestions += 1;
      }
    })
    checklistScore += group.score;
    checklistTotalScore += group.totalScore;
  })

  const total = checklistTotalScore > 0 ? `${(checklistScore/checklistTotalScore * 100).toFixed(2)}%`: '0.00%';

  return updateObject(state, { questionsTree, checklistScore: total, numberOfQuestionsThatShouldHaveAction })
}

const changeQuestionComment = (state: IEditServiceState, action: IAction<{ questionId: string, comment: string }>) => {
  const [groupIndex, questionIndex] = state.questionsTreeMap[action.payload.questionId];
  const questionsTree = state.questionsTree;
  questionsTree[groupIndex].questionArray[questionIndex].comment = action.payload.comment;
  return updateObject(state, { questionsTree })
}

const addQuestionAction = (state: IEditServiceState, action: IAction<{ questionId: string, action: IActionSigned }>) => {
  const [groupIndex, questionIndex] = state.questionsTreeMap[action.payload.questionId];
  const questionsTree = state.questionsTree;
  
  if (!questionsTree[groupIndex].questionArray[questionIndex].actions) {
    questionsTree[groupIndex].questionArray[questionIndex].actions = [];
  }
  questionsTree[groupIndex].questionArray[questionIndex].actions.push(action.payload.action);
  return updateObject(state, { questionsTree })
}

const editQuestionAction = (state: IEditServiceState, action: IAction<{ questionId: string, action: IActionSigned, actionId: string }>) => {
  const [groupIndex, questionIndex] = state.questionsTreeMap[action.payload.questionId];
  const questionsTree = state.questionsTree;
  const actionIndex = questionsTree[groupIndex].questionArray[questionIndex].actions.findIndex((actionA) => actionA._id === action.payload.actionId);
  questionsTree[groupIndex].questionArray[questionIndex].actions[actionIndex] = action.payload.action;
  return updateObject(state, { questionsTree })
}


const deleteQuestionAction = (state: IEditServiceState, action: IAction<{ questionId: string, actionId: string }>) => {
  const [groupIndex, questionIndex] = state.questionsTreeMap[action.payload.questionId];
  const questionsTree = state.questionsTree;
  const actionIndex = questionsTree[groupIndex].questionArray[questionIndex].actions.findIndex((actionA) => actionA._id === action.payload.actionId);
  questionsTree[groupIndex].questionArray[questionIndex].actions.splice(actionIndex, 1);
  return updateObject(state, { questionsTree })
}

const addQuestionFile = (state: IEditServiceState, action: IAction<{ questionId: string, files: IFile[] }>) => {
  const [groupIndex, questionIndex] = state.questionsTreeMap[action.payload.questionId];
  const questionsTree = [...state.questionsTree];
  if (questionsTree[groupIndex].questionArray[questionIndex].files) {
    questionsTree[groupIndex].questionArray[questionIndex].files = [
      ...questionsTree[groupIndex].questionArray[questionIndex].files,
      ...action.payload.files,
    ];
  } else {
    questionsTree[groupIndex].questionArray[questionIndex].files = [...action.payload.files];
  }
  return updateObject(state, { questionsTree })
}

const editQuestionFile = (state: IEditServiceState, action: IAction<{ questionId: string, file: IFile, fileId: string }>) => {
  const [groupIndex, questionIndex] = state.questionsTreeMap[action.payload.questionId];
  const questionsTree = [...state.questionsTree];
  const fileIndex = questionsTree[groupIndex].questionArray[questionIndex].files.findIndex((fileA) => fileA.id === action.payload.fileId);
  questionsTree[groupIndex].questionArray[questionIndex].files[fileIndex] = {
    ...questionsTree[groupIndex].questionArray[questionIndex].files[fileIndex],
    ...action.payload.file,
  };
  return updateObject(state, { questionsTree })
}

const deleteQuestionFile = (state: IEditServiceState, action: IAction<{ questionId: string, fileId: string }>) => {
  const [groupIndex, questionIndex] = state.questionsTreeMap[action.payload.questionId];
  const questionsTree = [...state.questionsTree];
  const actionIndex = questionsTree[groupIndex].questionArray[questionIndex].files.findIndex((fileA) => fileA.id === action.payload.fileId);
  questionsTree[groupIndex].questionArray[questionIndex].files.splice(actionIndex, 1);
  return updateObject(state, { questionsTree })
}

const addObservationFile = (state: IEditServiceState, action: IAction<{ files: IFile[] }>) => {
  const newFiles = [
    ...state.files,
    ...action.payload.files,
  ];
  return updateObject(state, { files: newFiles })
}

const editObservationFile = (state: IEditServiceState, action: IAction<{ file: IFile, fileId: string }>) => {
  const newFiles = [...state.files];
  const fileIndex = state.files.findIndex((fileA) => fileA.id === action.payload.fileId);
  newFiles[fileIndex] = {
    ...newFiles[fileIndex],
    ...action.payload.file,
  };
  return updateObject(state, { files: newFiles })
}

const deleteObservationFile = (state: IEditServiceState, action: IAction<{ fileId: string }>) => {
  const newFiles = [...state.files];
  const newState = newFiles.filter((file) => {
    const fileId = file._id ? file._id : file.id;
    return fileId !== action.payload.fileId;
  })
  return updateObject(state, { files: newState })
}

const answerAllQuestionsNa = (state: IEditServiceState) => {
  const questionsTree = [...state.questionsTree];
  
  questionsTree.forEach((group) => {
    group.score = 0;
    group.totalScore = 0;
    group.questionArray.forEach((question) => {
      question.answer = 'NA';
    })
  })

  return updateObject(state, { questionsTree, numberOfQuestionsThatShouldHaveAction: 0 })
}

// const addSignatureStart = (state: IEditServiceState) => {
//   return updateObject(state, { loadingSignature: true } )
// }

// const addSignatureSucess = (state: IEditServiceState, action: IAction<{signature: ISignature}>) => {
//   return updateObject(state, { loadingSignature: false, signature: action.payload.signature } )
// }

// const addSignatureFail = (state: IEditServiceState) => {
//   return updateObject(state, { loadingSignature: false } )
// }

const editServiceStart = (state: IEditServiceState) => {
  return updateObject(state, { postServiceLoading: true } )
}

const editServiceSuccess = (state: IEditServiceState, action: IAction<IService>) => {
  return updateObject(state, { postServiceLoading: false } )
}

const editServiceFail = (state: IEditServiceState) => {
  return updateObject(state, { postServiceLoading: false } )
}

const fetchOldActionsStart = (state: IEditServiceState) => {
  return updateObject(state, {
    questionsLoading: true,
  })
}

const fetchOldActionsSuccess = (state: IEditServiceState, action: IAction<IOldAction[]>) => {
  return updateObject(state, {
    oldActions: action.payload,
    questionsLoading: false,
  })
}

const fetchOldActionsFail = (state: IEditServiceState) => {
  return updateObject(state, {
    questionsLoading: false,
  })
}

const clearState = (state: IEditServiceState) => {
  return updateObject(state, {
    ...initialState
  })
}

const downloadReportStart = (state: IEditServiceState) => {
  return updateObject(state, {
    reportLoading: true,
  })
}

const downloadReportFail = (state: IEditServiceState) => {
  return updateObject(state, {
    reportLoading: false,
  })
}

const downloadReportSuccess = (state: IEditServiceState) => {
  return updateObject(state, {
    reportLoading: false,
  })
}

const editServiceReducer: Reducer<IEditServiceState, IAction<any>> = (state = initialState, action: IAction<any>) => {
  switch(action.type) {
    case actionTypes.EDIT_SERVICE_SELECT_SERVICE: return selectService(state, action);
    case actionTypes.EDIT_SERVICE_FETCH_SERVICE_START: return fetchServiceByIdStart(state);
    case actionTypes.EDIT_SERVICE_FETCH_SERVICE_SUCCESS: return fetchServiceByIdSuccess(state, action);
    case actionTypes.EDIT_SERVICE_FETCH_SERVICE_FAIL: return fetchServiceByIdFail(state);
    case actionTypes.EDIT_SERVICE_CHANGE_SERVICE_DATE: return changeServiceDate(state, action);
    case actionTypes.EDIT_SERVICE_CHANGE_SERVICE_DESCRIPTION: return changeServiceDescription(state, action);
    case actionTypes.EDIT_SERVICE_CHANGE_QUESTION_ANSWER: return changeQuestionAnswer(state, action);
    case actionTypes.EDIT_SERVICE_CHANGE_QUESTION_COMMENT: return changeQuestionComment(state, action);
    case actionTypes.EDIT_SERVICE_ADD_QUESTION_ACTION: return addQuestionAction(state, action);
    case actionTypes.EDIT_SERVICE_EDIT_QUESTION_ACTION: return editQuestionAction(state, action);
    case actionTypes.EDIT_SERVICE_DELETE_QUESTION_ACTION: return deleteQuestionAction(state, action);
    case actionTypes.EDIT_SERVICE_ADD_QUESTION_FILE: return addQuestionFile(state, action);
    case actionTypes.EDIT_SERVICE_EDIT_QUESTION_FILE: return editQuestionFile(state, action);
    case actionTypes.EDIT_SERVICE_DELETE_QUESTION_FILE: return deleteQuestionFile(state, action);
    case actionTypes.EDIT_SERVICE_ADD_FILE: return addObservationFile(state, action);
    case actionTypes.EDIT_SERVICE_EDIT_FILE: return editObservationFile(state, action);
    case actionTypes.EDIT_SERVICE_DELETE_FILE: return deleteObservationFile(state, action);
    case actionTypes.EDIT_SERVICE_ANSWER_ALL_QUESTIONS_NA: return answerAllQuestionsNa(state);
    // case actionTypes.EDIT_SERVICE_ADD_SIGNATURE_START: return addSignatureStart(state);
    // case actionTypes.EDIT_SERVICE_ADD_SIGNATURE_SUCCESS: return addSignatureSucess(state, action);
    // case actionTypes.EDIT_SERVICE_ADD_SIGNATURE_FAIL: return addSignatureFail(state);
    case actionTypes.EDIT_SERVICE_UPDATE_SERVICES_SUCCESS: return editServiceSuccess(state, action);
    case actionTypes.EDIT_SERVICE_REQUEST_SERVICES_START: return editServiceStart(state);
    case actionTypes.EDIT_SERVICE_REQUEST_SERVICES_FAIL: return editServiceFail(state);
    case actionTypes.EDIT_SERVICE_GET_OLD_ACTIONS_START: return fetchOldActionsStart(state);
    case actionTypes.EDIT_SERVICE_GET_OLD_ACTIONS_SUCCESS: return fetchOldActionsSuccess(state, action);
    case actionTypes.EDIT_SERVICE_GET_OLD_ACTIONS_FAIL: return fetchOldActionsFail(state);
    case actionTypes.EDIT_SERVICE_CLEAR_STATE: return clearState(state);
    case actionTypes.EDIT_SERVICE_DOWNLOAD_REPORT_START: return downloadReportStart(state);
    case actionTypes.EDIT_SERVICE_DOWNLOAD_REPORT_FAIL: return downloadReportFail(state);
    case actionTypes.EDIT_SERVICE_DOWNLOAD_REPORT_SUCCESS: return downloadReportSuccess(state);
    default: return state;
  }
}

export default editServiceReducer;
